import { DocumentTreeClassControl } from "@/Common/Helpers/DocumentTreeClassControl";
import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { DocumentUIState, TreeNodeTarget } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { DocumentExtensionContentComponentId } from "@/Documents/Extensions/DocumentExtensionContentRegistry";
import { DocumentExtensionViews } from "@/Documents/Models/UI/IDocumentsTabModel";
import { InboundFaxView } from "@/Documents/Models/UI/InboundFaxEnums";
import { DocumentsStore } from "@/Documents/Stores/DocumentsStore";

class DocumentTreeHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.Documents.Show_Create_Document, this.ShowCreateDocUI);
        this.RegisterOperation(LocalOperations.Documents.Tree_SubClass_Selection, this.SelectDocumentClass);
        this.RegisterOperation(LocalOperations.Documents.Tree_All_Documents_Selected, this.SelectAllDocuments);
        this.RegisterOperation(LocalOperations.Documents.Tree_ClearSelection, this.ClearSelection);
        this.RegisterOperation(LocalOperations.Documents.Tree_Load_Orphans, this.SearchOrphans);
        this.RegisterOperation(LocalOperations.Documents.Tree_Toggle_Parent, this.OnTreeParentClick);
        this.RegisterOperation(LocalOperations.Documents.Tree_Load_AssignedDocuments_Extension, this.SelectAssignedDocumentsExtension);
        this.RegisterOperation(LocalOperations.Documents.Tree_Load_DeactivatedAccount, this.SearchDeactivatedAccounts);
        this.RegisterOperation(LocalOperations.Documents.Tree_Load_Fax_Inbox, this.SelectInboundFaxesInbox);
        this.RegisterOperation(LocalOperations.Documents.Tree_Load_Fax_Trash, this.SelectInboundFaxesTrash);
        this.RegisterOperation(LocalOperations.Documents.Tree_Load_Fax_Indexed, this.SelectInboundFaxesIndexed);
    }

    public ShowCreateDocUI(request: LocalEvent, store: DocumentsStore): void {
        store.resetNewDocument();
        store.InitializeNewDocument();
        store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.Create);
    }

    private SelectDocumentClass(request: LocalEvent, store: DocumentsStore): void {
        var treeNodeTarget = request.Get<TreeNodeTarget>("target");

        if (store.DocumentsModel.get("UIState") !== DocumentUIState.Search) {
            store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.Search);
        }

        let isAllDocumentset = request.Get<boolean>("IsAllDocumentSet");

        if (isAllDocumentset)
            store.DocumentsModel = store.DocumentsModel.set("SelectedView", "allDocuments");

        if (treeNodeTarget === TreeNodeTarget.Myle_Documents_DocumentTree_SubClassSelection) {
            var docClassId = request.Get<string>("parentId");
            var subClassId = request.Get<string>("childId");

            if (docClassId && subClassId) {
                this.SetDocumentSelection(docClassId, subClassId, store);

                // create action to search documents
                store.DocumentActions.SearchDocuments(store.DocumentsModel, true);
            }
        }
    }

    private SelectAllDocuments(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel
            .set("UIState", DocumentUIState.Search)
            .set("SelectedView", "allDocuments")
            .set("SelectedDocumentClass", null)
            .set("SelectedDocumentSubclass", null)
            .set("DocumentSearchBreadCrumbTitle", cacheStore.GetString("AllDocumentsLabel"));

        store.DocumentActions.SearchDocuments(store.DocumentsModel);
        this.UnselectTreeChildren(store);

        // The DocumentsModel is sometimes cached and restored from the cacheStore.
        // Make sure it's updated for the new model.
        store.SaveState();
    }

    private ClearSelection(request: LocalEvent, store: DocumentsStore): void {
        this.SetDocumentSelection(null, null, store);
    }

    private UnselectTreeChildren(store: DocumentsStore): void {
        var classes = store.DocumentsModel.get("DocumentClasses").toArray();
        for (let docClass of classes) {
            var children = docClass.SubClasses.toArray();
            for (let child of children) {
                child.IsSelected = false;
            }
            docClass.SubClasses = Immutable.Map<string, DocumentClassModel>(children.map(item => [item.LookupCode, item]));
        }
        var map = Immutable.Map<string, DocumentClassModel>(classes.map(item => [item.LookupCode, item]));
        store.DocumentsModel = store.DocumentsModel.set("DocumentClasses", map);
    }

    private SetDocumentSelection(documentClassId: string, subclassId: string, store: DocumentsStore): void {
        // fetch selected docClass/subclass and set as selected in model
        var documentClass: DocumentClassModel = store.DocumentsModel.get("DocumentClasses").get(documentClassId);
        var documentSubClass: DocumentClassModel = (documentClass && documentClass.SubClasses) ? documentClass.SubClasses.get(subclassId) : null;

        if (documentClass)
            store.DocumentsModel = store.DocumentsModel.set("SelectedDocumentClass", documentClass);
        else
            store.DocumentsModel = store.DocumentsModel.set("SelectedDocumentClass", null);

        if (documentSubClass) {
            store.DocumentsModel = store.DocumentsModel.set("SelectedDocumentSubclass", documentSubClass);
        }
        else
            store.DocumentsModel = store.DocumentsModel.set("SelectedDocumentSubclass", null);

        // refresh model DocumentClasses with selected subclasses
        var newDocClasses = DocumentTreeClassControl.GetDocumentClasses();

        if (documentClass && documentSubClass) {
            // this will set the selected sub class
            documentClass = newDocClasses.get(documentClassId);
            documentClass.IsExpanded = true;
            documentClass.IsSelected = true;
            documentSubClass.IsSelected = true;
            documentSubClass.IsSelected = true;
            documentClass.SubClasses = documentClass.SubClasses.update(subclassId, () => documentSubClass);
            newDocClasses = newDocClasses.update(documentClassId, () => documentClass);

            // set the breadcrump
            store.DocumentsModel = store.DocumentsModel.set("DocumentSearchBreadCrumbTitle", documentClass.Name + " - " + documentSubClass.Name);
        }
        store.DocumentsModel = store.DocumentsModel.set("DocumentClasses", newDocClasses);
    }

    private SelectInboundFaxesInbox(request: LocalEvent, store: DocumentsStore): void {
        if (store.DocumentsModel.get("UIState") !== DocumentUIState.InboundFaxInboxMailbox)
            store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.InboundFaxInboxMailbox);

        const currentMailboxState = store.InboundFaxInboxModel;

        store.DocumentsModel = store.DocumentsModel
            .set("SelectedView", "faxInbox")
            .set("SelectedDocumentClass", null)
            .set("SelectedDocumentSubclass", null);
        store.InboundFaxInboxModel = { ...currentMailboxState, currentView: InboundFaxView.FaxesList, currentFax: null };
        this.UnselectTreeChildren(store);
    }

    private SelectInboundFaxesTrash(request: LocalEvent, store: DocumentsStore): void {
        if (store.DocumentsModel.get("UIState") !== DocumentUIState.InboundFaxTrashMailbox)
            store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.InboundFaxTrashMailbox);

        const currentMailboxState = store.InboundFaxTrashModel;

        store.DocumentsModel = store.DocumentsModel
            .set("SelectedView", "faxTrash")
            .set("SelectedDocumentClass", null)
            .set("SelectedDocumentSubclass", null);

        store.InboundFaxTrashModel = { ...currentMailboxState, currentView: InboundFaxView.FaxesList };

        this.UnselectTreeChildren(store);
    }

    private SelectInboundFaxesIndexed(request: LocalEvent, store: DocumentsStore): void {
        if (store.DocumentsModel.get("UIState") !== DocumentUIState.InboundFaxIndexedMailbox)
            store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.InboundFaxIndexedMailbox);

        const currentMailboxState = store.InboundFaxIndexedModel;

        store.DocumentsModel = store.DocumentsModel
            .set("SelectedView", "faxIndexed")
            .set("SelectedDocumentClass", null)
            .set("SelectedDocumentSubclass", null);

        store.InboundFaxIndexedModel = { ...currentMailboxState, currentView: InboundFaxView.FaxesList };

        this.UnselectTreeChildren(store);
    }

    private SearchOrphans(request: LocalEvent, store: DocumentsStore): void {
        if (store.DocumentsModel.get("UIState") !== DocumentUIState.Search) {
            store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.Search);
        }
        store.DocumentsModel = store.DocumentsModel.set("SelectedView", "orphan");
        store.DocumentsModel = store.DocumentsModel.set("DocumentSearchBreadCrumbTitle", cacheStore.GetString("OrphansLabel"));

        store.DocumentActions.SearchOrphanDocuments(store.DocumentsModel);
        this.UnselectTreeChildren(store);
    }

    private SelectAssignedDocumentsExtension(request: LocalEvent, store: DocumentsStore): void {
        const extensionName = request.Get<DocumentExtensionViews>("extensionName");
        if (!Object.keys(DocumentExtensionContentComponentId).includes(extensionName)) {
            throw new Error(`SelectAssignedDocumentsExtension: Unknown extension name '${extensionName}'`);
        }

        if (store.DocumentsModel.get("UIState") !== DocumentUIState.Extension) {
            store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.Extension);
        }

        store.DocumentsModel = store.DocumentsModel.set("SelectedView", extensionName);
        this.UnselectTreeChildren(store);
    }

    private OnTreeParentClick(request: LocalEvent, store: DocumentsStore): void {
        var nodeId = request.Get<string>("value");
        var treeList = store.DocumentsModel.get("DocumentClasses").toArray().map(node => {
            if (node.LookupId === nodeId) {
                node.SetExpanded(!node.IsExpanded);
            }
            else {
                node.SetExpanded(false);
            }
            return node;
        });
        store.DocumentsModel = store.DocumentsModel.set("DocumentClasses", Immutable.Map<string, DocumentClassModel>(treeList.map(item => [item.LookupCode, item])));
    }

    private SearchDeactivatedAccounts(request: LocalEvent, store: DocumentsStore): void {
        if (store.DocumentsModel.get("UIState") !== DocumentUIState.Search) {
            store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.Search);
        }
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocumentClass", null);
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocumentSubclass", null);
        store.DocumentsModel = store.DocumentsModel.set("SelectedView", "deactivatedAccount");
        store.DocumentsModel = store.DocumentsModel.set("DocumentSearchBreadCrumbTitle", cacheStore.GetString("DeactivatedAccountsLabel"));
        store.DocumentsModel = store.DocumentsModel.set("DeactivatedAccountCliniciansCache", store.getDefaultGlobalCliniciansCache(true));

        store.DocumentActions.SearchDocuments(store.DocumentsModel);
        this.UnselectTreeChildren(store);
    }
}

export { DocumentTreeHandler };
